import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PhotoUpload from "@/components/PhotoUpload";
import SiteOperating from "@/components/SiteOperating";
import { toast } from "sonner";
import { Building2, Camera, Save, MapPin, Plus, Trash2, Clock } from "lucide-react";

export default function Settings() {
  const { user, has } = useAuth();
  const [org, setOrg] = useState(null);
  const [venues, setVenues] = useState([]);
  const [venueName, setVenueName] = useState("");
  const [opVenue, setOpVenue] = useState("");

  useEffect(() => {
    api.get("/organisation").then((r) => setOrg(r.data)).catch(() => {});
    api.get("/venues").then((r) => { setVenues(r.data); if (r.data[0]) setOpVenue(r.data[0].venue_id); }).catch(() => {});
  }, []);
  if (!org) return null;

  const save = async () => { await api.put("/organisation", org); toast.success("Settings saved"); };
  const addVenue = async () => { if (!venueName) return; await api.post("/venues", { name: venueName }); setVenueName(""); setVenues((await api.get("/venues")).data); toast.success("Venue added"); };
  const removeVenue = async (vid) => { await api.delete(`/venues/${vid}`); setVenues((await api.get("/venues")).data); toast.success("Venue removed"); };

  return (
    <div className="max-w-2xl">
      <PageHeader title="Settings" subtitle="Configure your organisation and defaults." testid="settings-header" />

      <div className="cc-card p-6 space-y-4">
        <div className="flex items-center gap-2 font-display font-bold text-slate-800"><Building2 size={18} className="text-brand" /> Organisation</div>
        <div>
          <Label className="text-xs font-semibold text-slate-600 mb-1.5 block">Logo (shown on dashboard & PDF reports)</Label>
          <div className="max-w-xs"><PhotoUpload value={org.logo} onChange={(fid) => setOrg({ ...org, logo: fid })} label="Upload logo" testid="org-logo" /></div>
        </div>
        <div><Label className="text-xs font-semibold text-slate-600">Business name</Label><Input value={org.name || ""} onChange={(e) => setOrg({ ...org, name: e.target.value })} data-testid="org-name" className="mt-1.5" /></div>
        <div><Label className="text-xs font-semibold text-slate-600">Address</Label><Textarea value={org.address || ""} onChange={(e) => setOrg({ ...org, address: e.target.value })} data-testid="org-address" className="mt-1.5" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs font-semibold text-slate-600">Contact email</Label><Input value={org.contact_email || ""} onChange={(e) => setOrg({ ...org, contact_email: e.target.value })} data-testid="org-email" className="mt-1.5" /></div>
          <div><Label className="text-xs font-semibold text-slate-600">Contact phone</Label><Input value={org.contact_phone || ""} onChange={(e) => setOrg({ ...org, contact_phone: e.target.value })} data-testid="org-phone" className="mt-1.5" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs font-semibold text-slate-600">Timezone</Label><Input value={org.timezone || ""} onChange={(e) => setOrg({ ...org, timezone: e.target.value })} data-testid="org-timezone" className="mt-1.5" /></div>
          <div><Label className="text-xs font-semibold text-slate-600">Temperature unit</Label><Input value={org.temp_unit || ""} onChange={(e) => setOrg({ ...org, temp_unit: e.target.value })} data-testid="org-unit" className="mt-1.5" /></div>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-600 flex items-center gap-1"><Camera size={13} /> Cleaning photo verification: {org.photo_verification_probability}%</Label>
          <input type="range" min="0" max="100" step="5" value={org.photo_verification_probability} onChange={(e) => setOrg({ ...org, photo_verification_probability: Number(e.target.value) })} data-testid="org-photo-prob" className="w-full mt-2 accent-brand" />
        </div>
        <Button onClick={save} data-testid="save-settings" className="bg-brand hover:bg-brand-hover rounded-xl"><Save size={16} className="mr-1" /> Save settings</Button>
      </div>

      <div className="cc-card p-6 mt-6 space-y-3" data-testid="venues-card">
        <div className="flex items-center gap-2 font-display font-bold text-slate-800"><MapPin size={18} className="text-brand" /> Venues / sites</div>
        <p className="text-xs text-slate-500">Tag temperature, cleaning and checklist records to a venue so multi-site owners can filter by location.</p>
        {venues.map((v) => (
          <div key={v.venue_id} className="flex items-center justify-between border rounded-lg p-2.5 text-sm" data-testid={`settings-venue-${v.venue_id}`}>
            <span className="font-medium text-slate-700">{v.name}</span>
            <button onClick={() => removeVenue(v.venue_id)} data-testid={`remove-venue-${v.venue_id}`} className="text-red-400"><Trash2 size={15} /></button>
          </div>
        ))}
        {venues.length === 0 && <div className="text-sm text-slate-400">No venues yet.</div>}
        <div className="flex gap-2 pt-1">
          <Input placeholder="Venue name" value={venueName} onChange={(e) => setVenueName(e.target.value)} data-testid="new-venue-name" />
          <Button onClick={addVenue} data-testid="add-venue" className="bg-brand hover:bg-brand-hover"><Plus size={14} /></Button>
        </div>
      </div>

      {venues.length > 0 && (
        <div className="cc-card p-6 mt-6 space-y-3" data-testid="operating-hours-card">
          <div className="flex items-center gap-2 font-display font-bold text-slate-800"><Clock size={18} className="text-brand" /> Opening hours & closures</div>
          <p className="text-xs text-slate-500">Tell CaterCTRL when each site operates so it won't flag missing compliance on legitimately closed days.</p>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Site</Label>
            <select value={opVenue} onChange={(e) => setOpVenue(e.target.value)} data-testid="operating-venue-select"
              className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
              {venues.map((v) => <option key={v.venue_id} value={v.venue_id}>{v.name}</option>)}
            </select>
          </div>
          {opVenue && <SiteOperating venueId={opVenue}
            canManageSchedule={has("manage_site_operating_status")}
            canManageClosures={has("manage_site_operating_status")} />}
        </div>
      )}
    </div>
  );
}
